import React, { useEffect, useState, useRef } from "react"
import {
    Alert,
    Card,
    CardBody,
    Col,
    Form,
    FormGroup,
    Row,
    Label,
} from "reactstrap"
import { useFormik } from "formik"
import { useDispatch, useSelector } from "react-redux"
import * as Yup from "yup"
import { validateBeforeConfirm } from "helpers/validation_helper"
import {
    getOfferExclusions,
    manageOfferExclusions,
    clearOfferExclusions,
    addExclusionToForm,
    updateExclusionInForm,
    removeExclusionFromForm,
    clearExclusionForm,
} from "store/offers/actions"
import ConfirmButton from "components/Common/ConfirmButton"
import CButton from "components/Common/CButton"
import IconButton from "components/Common/IconButton"
import AccessControlWrapper from "components/Common/PermissionButton"
import * as url from "../../helpers/url_helper"
import TextInput from "components/Common/TextInput"

export default function OfferExclusionsForm({
    page,
    match,
    history,
    offerType,
    canEdit,
    onTabCompletion,
    activeTab,
    tabId,
    createdOfferID,
    copyFromID,
    fromType,
}) {
    const [showAddForm, setShowAddForm] = useState(page === "add")
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [editingExclusionIndex, setEditingExclusionIndex] = useState(null)
    const [isEditMode, setIsEditMode] = useState(false)
    const [currentExclusion, setCurrentExclusion] = useState({
        exclusionText: "",
    })

    const prevExclusionCreationSuccess = useRef(null)
    const prevOfferExclusionsError = useRef(null)
    const hasFetchedCopyData = useRef(false) // Track if we've fetched data from copied offer

    const dispatch = useDispatch()
    const detailID = match?.params?.id && match?.params?.id !== "add" ? match.params.id : null

    const offerID = page === "add" ? createdOfferID : detailID
    
    // For copy functionality, use the source offer ID to load exclusion data initially,
    // but switch to createdOfferID once the offer is created
    const sourceOfferID = (copyFromID && page === "add" && !createdOfferID) ? copyFromID : offerID

    // Validation schema
    const validationSchema = Yup.object({
        exclusionText: Yup.string()
            .required("Exclusion text is required")
            .max(500, "Exclusion text cannot exceed 500 characters")
            .trim("Exclusion text cannot contain leading or trailing spaces"),
    })

    // Form validation using Formik
    const validation = useFormik({
        enableReinitialize: true,
        initialValues: {
            exclusionText: currentExclusion.exclusionText,
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            const isValid = await validateBeforeConfirm(validation)
            if (isValid) {
                handleAddExclusionToForm()
            }
        },
    })

    // Update form values when currentExclusion changes
    useEffect(() => {
        validation.setValues({
            exclusionText: currentExclusion.exclusionText,
        })
    }, [currentExclusion])

    const shouldDisplayError = error => {
        if (!error) return false

        const errorMessage = error.message || error.description || ""
        const errorCode = error.code || error.status || ""

        const noDataPatterns = ["no content available"]

        return !noDataPatterns.some(
            pattern =>
                errorMessage.toLowerCase().includes(pattern) ||
                errorCode.toString().includes("404")
        )
    }

    const {
        offerExclusions,
        offerExclusionsError,
        isLoadingOfferExclusions,
        exclusionFormData,
        exclusionCreationSuccess,
    } = useSelector(state => ({
        offerExclusions: state.offers.offerExclusions || [],
        offerExclusionsError: state.offers.offerExclusionsError,
        isLoadingOfferExclusions: state.offers.isLoadingOfferExclusions || false,
        exclusionFormData: state.offers.exclusionFormData || [],
        exclusionCreationSuccess: state.offers.exclusionCreationSuccess,
    }))

    // Pre-fetch offer exclusions when copying (even if tab is not active yet)
    useEffect(() => {
        // When copying, fetch data from source offer immediately
        if (copyFromID && page === "add" && !createdOfferID && !hasFetchedCopyData.current) {
            hasFetchedCopyData.current = true
            dispatch(
                getOfferExclusions({
                    headers: {
                        "input-parameters": { offerID: copyFromID },
                    },
                })
            )
        }
    }, [dispatch, copyFromID, page, createdOfferID])

    // Fetch offer exclusions when tab is active (for detail mode or after save)
    useEffect(() => {
        const isTabActive = activeTab === tabId

        if (sourceOfferID && isTabActive && page === "detail") {
            dispatch(
                getOfferExclusions({
                    headers: {
                        "input-parameters": { offerID: sourceOfferID },
                    },
                })
            )
        }
    }, [dispatch, sourceOfferID, activeTab, tabId, page])

    useEffect(() => {
        dispatch(clearExclusionForm())
        // Clear offer exclusions data when in add mode without copy to prevent showing stale data
        if (page === "add" && !copyFromID) {
            dispatch(clearOfferExclusions())
        }
    }, [dispatch, page, copyFromID])

    useEffect(() => {
        return () => {
            dispatch(clearOfferExclusions())
        }
    }, [dispatch])

    // Populate form data from fetched exclusions when copying
    useEffect(() => {
        if (copyFromID && page === "add" && offerExclusions.length > 0 && exclusionFormData.length === 0) {
            offerExclusions.forEach(exclusion => {
                const exclusionText = typeof exclusion === 'string' ? exclusion : exclusion.exclusionText
                if (exclusionText) {
                    dispatch(addExclusionToForm({ exclusionText }))
                }
            })
        }
    }, [copyFromID, page, offerExclusions, exclusionFormData.length, dispatch])

    const handleAddExclusionToForm = async () => {
        // Validate form before proceeding
        const isValid = await validateBeforeConfirm(validation)
        if (!isValid) {
            return
        }

        const newExclusion = {
            exclusionText: validation.values.exclusionText.trim(),
        }

        if (editingExclusionIndex !== null) {
            dispatch(updateExclusionInForm(editingExclusionIndex, newExclusion))
            setEditingExclusionIndex(null)
        } else {
            dispatch(addExclusionToForm(newExclusion))
        }

        // Reset form
        setCurrentExclusion({ exclusionText: "" })
        validation.resetForm()
    }

    const handleEditExclusion = (exclusion, index) => {
        setCurrentExclusion({
            exclusionText: exclusion.exclusionText,
        })
        setEditingExclusionIndex(index)
    }

    const handleDeleteExclusionFromForm = (index) => {
        dispatch(removeExclusionFromForm(index))

        if (editingExclusionIndex === index) {
            setEditingExclusionIndex(null)
            setCurrentExclusion({ exclusionText: "" })
            validation.resetForm()
        }
    }

    const handleCancelEdit = () => {
        setEditingExclusionIndex(null)
        setCurrentExclusion({ exclusionText: "" })
        validation.resetForm()
    }

    const handleFinalSubmit = () => {
        if (isSubmitting) return // Prevent multiple submissions
        
        setIsSubmitting(true)

        const payload = {
            offerID: parseInt(offerID),
            offerExclusions: exclusionFormData.map(exclusion => exclusion.exclusionText),
        }

        dispatch(manageOfferExclusions(payload, history))
    }

    // Handle success states
    useEffect(() => {
        if (exclusionCreationSuccess && exclusionCreationSuccess !== prevExclusionCreationSuccess.current) {
            setIsSubmitting(false)
            setIsEditMode(false)
            hasFetchedCopyData.current = false // Data saved, show saved listing now

            if (page !== "add") {
                setShowAddForm(false)
            }

            if (page === "add" && onTabCompletion) {
                onTabCompletion("4")
            }

            if (offerID) {
                dispatch(
                    getOfferExclusions({
                        headers: {
                            "input-parameters": { offerID: offerID },
                        },
                    })
                )
            }

            prevExclusionCreationSuccess.current = exclusionCreationSuccess
        }
    }, [exclusionCreationSuccess, page, offerID, dispatch, onTabCompletion])

    // Separate effect for error handling
    useEffect(() => {
        if (offerExclusionsError && offerExclusionsError !== prevOfferExclusionsError.current) {
            setIsSubmitting(false)
            prevOfferExclusionsError.current = offerExclusionsError
        }
    }, [offerExclusionsError])


    const handleAddNewExclusion = () => {
        dispatch(clearExclusionForm())
        setCurrentExclusion({ exclusionText: "" })
        setEditingExclusionIndex(null)
        setIsEditMode(false)
        setShowAddForm(true)
        validation.resetForm()
    }

    const handleEditExistingExclusions = () => {
        dispatch(clearExclusionForm())

        offerExclusions.forEach(exclusion => {
            dispatch(addExclusionToForm({
                exclusionText: exclusion
            }))
        })

        setIsEditMode(true)
        setShowAddForm(true)
        setCurrentExclusion({ exclusionText: "" })
        setEditingExclusionIndex(null)
        validation.resetForm()
    }

    const handleCancelForm = () => {
        dispatch(clearExclusionForm())
        setCurrentExclusion({ exclusionText: "" })
        setEditingExclusionIndex(null)
        setIsEditMode(false)
        validation.resetForm()
        if (page !== "add") {
            setShowAddForm(false)
        }
    }


    // Handle input changes
    const handleInputChange = (field, value) => {
        setCurrentExclusion(prev => ({
            ...prev,
            [field]: value
        }))
        validation.setFieldValue(field, value)
    }

    // Create a proper validation object for read-only fields
    const getValidationForReadOnly = () => ({
        errors: {},
        touched: {},
        values: {},
        setFieldValue: () => { },
        handleBlur: () => { },
        isValid: true,
    })

    const getHeaderButton = () => {
        if (page === "add" || showAddForm) {
            return null
        }

        if (!canEdit) {
            return null
        }

        if (offerExclusions.length === 0) {
            return (
                <AccessControlWrapper
                    contextUri={url.MANAGE_OFFER_EXCLUSIONS}
                    actionType="post"
                >
                    <CButton.Success
                        icon="bx bx-plus"
                        text="Add Exclusions"
                        onClick={handleAddNewExclusion}
                    />
                </AccessControlWrapper>
            )
        } else {
            return (
                <AccessControlWrapper
                    contextUri={url.MANAGE_OFFER_EXCLUSIONS}
                    actionType="post"
                >
                    <CButton.Primary
                        text="Edit Exclusions"
                        onClick={handleEditExistingExclusions}
                    />
                </AccessControlWrapper>
            )
        }
    }


   
    return (
        <>
            {/* Error Display */}
            {offerExclusionsError && shouldDisplayError(offerExclusionsError) && (
                <Alert color="danger" className="mb-3">
                    {offerExclusionsError.message || "An error occurred"}
                </Alert>
            )}

            {/* Add/Edit Exclusion Form */}
            {showAddForm && (
                <Card className="mb-4">
                    <CardBody>
                        <h6 className="mb-3">
                            {isEditMode ? "Edit Exclusions" : (exclusionFormData.length > 0 ? "Manage Exclusions" : "Add Exclusions")}
                        </h6>

                        <Form
                            className="needs-validation"
                            onSubmit={e => {
                                e.preventDefault()
                                validation.handleSubmit()
                                return false
                            }}
                        >
                            {exclusionFormData.map((exclusion, index) => (
                                <Row key={index} className="align-items-end mb-2">
                                    <Col md="8">
                                        <TextInput
                                            name={`exclusionText_${index}`}
                                            label="Exclusion"
                                            placeholder="Enter exclusion text"
                                            value={editingExclusionIndex === index ? currentExclusion.exclusionText : exclusion.exclusionText}
                                            readOnly={editingExclusionIndex !== index || isSubmitting}
                                            maxLength={500}
                                            required={editingExclusionIndex === index}
                                            onChange={(e) => {
                                                if (editingExclusionIndex === index) {
                                                    handleInputChange('exclusionText', e.target.value)
                                                }
                                            }}
                                            validation={editingExclusionIndex === index ? validation : getValidationForReadOnly()}
                                            inputClassName={editingExclusionIndex !== index ? "text-dark" : ""}
                                        />
                                    </Col>
                                    <Col md="4" className="d-flex align-items-end">
                                        <FormGroup className="d-flex gap-2 mb-2">
                                            {editingExclusionIndex === index ? (
                                                <>
                                                    <IconButton.Save
                                                        onClick={handleAddExclusionToForm}
                                                        title="Update exclusion"
                                                        disabled={isSubmitting || !validation.isValid || !currentExclusion.exclusionText.trim()}
                                                    />
                                                    <IconButton.Cancel
                                                        onClick={handleCancelEdit}
                                                        title="Cancel edit"
                                                        disabled={isSubmitting}
                                                    />
                                                </>
                                            ) : (
                                                <>
                                                    <button
                                                        type="button"
                                                        className="btn btn-primary btn-sm"
                                                        onClick={() => handleEditExclusion(exclusion, index)}
                                                        title="Edit exclusion"
                                                        disabled={isSubmitting}
                                                    >
                                                        <i className="bx bx-edit"></i>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-danger btn-sm"
                                                        onClick={() => handleDeleteExclusionFromForm(index)}
                                                        title="Delete exclusion"
                                                        disabled={isSubmitting}
                                                    >
                                                        <i className="bx bx-trash"></i>
                                                    </button>
                                                </>
                                            )}
                                        </FormGroup>
                                    </Col>
                                </Row>
                            ))}

                            {editingExclusionIndex === null && (
                                <Row className="align-items-center mb-3">
                                    <Col md="8">
                                        <TextInput
                                            name="exclusionText"
                                            label="Exclusion"
                                            placeholder="Enter exclusion text"
                                            value={currentExclusion.exclusionText}
                                            readOnly={isSubmitting}
                                            maxLength={500}
                                            required={true}
                                            onChange={(e) => handleInputChange('exclusionText', e.target.value)}
                                            validation={validation}
                                        />
                                    </Col>
                                    <Col md="4" className="d-flex align-items-end">
                                        <FormGroup className="d-flex gap-2 mt-3">
                                            <IconButton.Add
                                                onClick={handleAddExclusionToForm}
                                                disabled={isSubmitting || !validation.isValid || !currentExclusion.exclusionText.trim()}
                                                title="Add exclusion"
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                            )}
                        </Form>

                        {/* Form Action Buttons */}
                        <div className="text-end">
                            {page === "detail" && (
                                <CButton.Secondary
                                    text="Cancel"
                                    className="me-2"
                                    onClick={handleCancelForm}
                                    disabled={isSubmitting}
                                />
                            )}

                            {(exclusionFormData.length > 0 || isEditMode) && (
                                <ConfirmButton
                                    contextUri={url.MANAGE_OFFER_EXCLUSIONS}
                                    actionType="post"
                                    validateBeforeConfirm={() =>
                                        Promise.resolve(isEditMode || exclusionFormData.length > 0)
                                    }
                                    onConfirm={handleFinalSubmit}
                                    message={`Are you sure you want to ${isEditMode ? 'update' : 'save'} these exclusions?`}
                                    disabled={isSubmitting}
                                    renderButton={
                                        <CButton.Primary
                                            text={isEditMode ? "Update Exclusions" : "Save Exclusions"}
                                            loading={isSubmitting}
                                            disabled={isSubmitting}
                                        />
                                    }
                                />
                            )}
                        </div>
                    </CardBody>
                </Card>
            )}

            {/* // Don't show saved listing when copying (data is from source offer, not saved yet) */}
            {!isEditMode && !hasFetchedCopyData.current && (
                <Card>
                    <CardBody>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="mb-0">Exclusions</h5>
                            {getHeaderButton()}
                        </div>
                        
                        {isLoadingOfferExclusions ? (
                            <div className="text-center py-4">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : offerExclusions.length > 0 ? (
                            <ul className="ps-3">
                                {offerExclusions.map((exclusion, index) => (
                                    <li key={index} className="py-1">
                                        {exclusion}
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <div className="text-center py-4">
                                <i className="bx bx-info-circle display-1 text-muted"></i>
                                <p className="text-muted mt-3">No exclusions added yet</p>
                            </div>
                        )}
                    </CardBody>
                </Card>
            )}
        </>
    )
}